import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUp';
import TermsAgreementPage from './pages/TermsAgreement';
import UserProfilePage from './pages/UserProfile';
import SampleVideosPage from './pages/SampleVideosPage';
import ThankYouPage from './pages/ThankYouPage';
import VerifyEmailPage from './pages/VerifyEmail';
import DashboardPage from './pages/DashboardPage';
import BlogsPage from './pages/BlogsPage';
import ReportsPage from './pages/ReportsPage';
import PublicBlogPage from './pages/PublicBlogPage';
import TestCommentPage from './pages/TestCommentPage';
import UserSettingsPage from './pages/UserSettingsPage';
import Navbar from './components/Navbar';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Public Route Component (redirect if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return !isAuthenticated ? children : <Navigate to="/dashboard" />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
              <Route 
                path="/login" 
                element={
                  <PublicRoute>
                    <LoginPage />
                  </PublicRoute>
                } 
              />
              <Route 
                path="/signup" 
                element={<SignUpPage />} 
              />
              <Route
                path="/terms-agreement"
                element={<TermsAgreementPage />}
              />
              <Route
                path="/user-profile"
                element={<UserProfilePage />}
              />
              <Route
                path="/sample-videos"
                element={<SampleVideosPage />}
              />
              <Route
                path="/thank-you"
                element={<ThankYouPage />}
              />
              <Route
                path="/verify-email/:token"
                element={<VerifyEmailPage />}
              />
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <Navbar />
                    <DashboardPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/blogs" 
                element={
                  <ProtectedRoute>
                    <Navbar />
                    <BlogsPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/reports" 
                element={
                  <ProtectedRoute>
                    <Navbar />
                    <ReportsPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/settings" 
                element={
                  <ProtectedRoute>
                    <Navbar />
                    <UserSettingsPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/blog/:slug" 
                element={<PublicBlogPage />} 
              />
              <Route 
                path="/test-comment" 
                element={<TestCommentPage />} 
              />
              <Route path="/" element={<Navigate to="/dashboard" />} />
            </Routes>
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
